import React, { useState } from "react";
import { Objective } from "../lib/objectives-store";
import {
  formatTimestamp,
  ObjectiveProgress,
  parseTimestamp,
} from "../lib/progress-store";

interface Props {
  objective: Objective;
  progress: ObjectiveProgress | undefined;
  onToggle: (checked: boolean) => void;
  onSeek: (seconds: number) => void;
  onEdit: (patch: Partial<Objective>) => void;
  onRemove: () => void;
}

export default function ObjectiveItem({
  objective,
  progress,
  onToggle,
  onSeek,
  onEdit,
  onRemove,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(objective.title);
  const [draftDetail, setDraftDetail] = useState(objective.detail);
  const [draftTimestamp, setDraftTimestamp] = useState(
    formatTimestamp(objective.timestampSec),
  );
  const [timestampError, setTimestampError] = useState<string | null>(null);
  const checked = progress?.checked ?? false;

  function startEditing() {
    setDraftTitle(objective.title);
    setDraftDetail(objective.detail);
    setDraftTimestamp(formatTimestamp(objective.timestampSec));
    setTimestampError(null);
    setEditing(true);
  }

  function save() {
    const parsed = parseTimestamp(draftTimestamp);
    if (parsed === "invalid") {
      setTimestampError("Use m:ss, mm:ss, h:mm:ss, or a number of seconds.");
      return;
    }
    setTimestampError(null);
    const patch: Partial<Objective> = {
      title: draftTitle.trim() || objective.title,
      detail: draftDetail.trim(),
    };
    if (parsed !== objective.timestampSec) {
      patch.timestampSec = parsed;
    }
    onEdit(patch);
    setEditing(false);
  }

  if (editing) {
    return (
      <li
        data-testid="objective-item"
        data-checked={checked}
        className="p-3 rounded border border-gray-700 bg-gray-900 space-y-2"
      >
        <input
          type="text"
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          aria-label="Objective title"
          className="w-full px-2 py-1 bg-gray-950 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-orange-500"
        />
        <textarea
          value={draftDetail}
          onChange={(e) => setDraftDetail(e.target.value)}
          rows={2}
          aria-label="Objective detail"
          className="w-full px-2 py-1 bg-gray-950 border border-gray-700 rounded text-xs text-gray-300 focus:outline-none focus:border-orange-500"
        />
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Timestamp</label>
          <input
            type="text"
            value={draftTimestamp}
            onChange={(e) => {
              setDraftTimestamp(e.target.value);
              if (timestampError) setTimestampError(null);
            }}
            placeholder="m:ss"
            aria-label="Objective timestamp"
            className="w-24 px-2 py-1 bg-gray-950 border border-gray-700 rounded text-xs text-gray-300 font-mono focus:outline-none focus:border-orange-500"
          />
          <span className="text-xs text-gray-600">leave blank for none</span>
        </div>
        {timestampError && (
          <p className="text-xs text-red-400">{timestampError}</p>
        )}
        <div className="flex gap-2 text-xs">
          <button
            type="button"
            onClick={save}
            className="px-2.5 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setDraftTitle(objective.title);
              setDraftDetail(objective.detail);
              setDraftTimestamp(formatTimestamp(objective.timestampSec));
              setTimestampError(null);
              setEditing(false);
            }}
            className="px-2.5 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded"
          >
            Cancel
          </button>
        </div>
      </li>
    );
  }

  return (
    <li
      data-testid="objective-item"
      data-checked={checked}
      className="group relative flex items-start gap-3 p-3 rounded border border-gray-800 bg-gray-900 transition hover:border-gray-700"
    >
      <div className="shrink-0 w-12 pt-0.5">
        {objective.timestampSec != null ? (
          <button
            type="button"
            onClick={() => onSeek(objective.timestampSec!)}
            aria-label={`Jump to ${formatTimestamp(objective.timestampSec)}`}
            className="font-mono text-xs text-orange-300 hover:text-orange-200 transition tabular-nums"
            title="Jump to this moment"
          >
            {formatTimestamp(objective.timestampSec)}
          </button>
        ) : (
          <span className="font-mono text-xs text-gray-700 tabular-nums">
            —:—
          </span>
        )}
      </div>

      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        aria-label={`Mark "${objective.title}" complete`}
        className="mt-0.5 w-4 h-4 accent-orange-500 shrink-0 cursor-pointer"
      />

      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium leading-snug transition ${
            checked ? "text-gray-500 line-through" : "text-gray-100"
          }`}
        >
          {objective.title}
        </p>
        {objective.detail && (
          <p
            className={`text-xs leading-relaxed mt-1 transition ${
              checked ? "text-gray-600" : "text-gray-400"
            }`}
          >
            {objective.detail}
          </p>
        )}

        <div className="flex items-center gap-3 mt-2 text-xs opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
          <button
            type="button"
            onClick={startEditing}
            className="text-gray-500 hover:text-gray-300 transition"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-500 hover:text-red-400 transition"
          >
            Remove
          </button>
        </div>
      </div>
    </li>
  );
}
